import React, { useEffect, useRef } from 'react'
import { validateInput } from 'src/helpers/input'
import PropTypes from 'prop-types'

/**
 * @function ElementByNameGetter
 * @param {string} name - the name of the input
 * @returns {Node} - the dom element with that name
 /*

 /**
 * validate list of inputs with their default and custom conditions. Useful in onSubmit and initial form mount
 *
 * @param {ElementByNameGetter} elByName - function that returns the element
 * @param {FieldDescription[]} inputs - the inputs in the form
 * @returns {Object} ({[name]: value}) - form data
 */
export const validateForm = (elByName, inputs) => {
  const data = {}

  inputs.forEach(({ name, conditions, parse }) => {
    const el = elByName(name)
    const [, value] = validateInput(el, conditions, parse)
    data[name] = value
  })

  return data
}

const Condition = PropTypes.shape({
  message: PropTypes.string.isRequired,
  validate: PropTypes.func.isRequired
})

const InputValidation = PropTypes.shape({
  name: PropTypes.string.isRequired,
  conditions: PropTypes.arrayOf(Condition).isRequired,
  parse: PropTypes.func
})

const propTypes = {
  className: PropTypes.string,
  validations: PropTypes.arrayOf(InputValidation),
  names: PropTypes.arrayOf(PropTypes.string),
  onSubmit: PropTypes.func.isRequired
}

const Form = ({ names, validations, onSubmit, ...restProps }) => {
  const root = useRef(null)

  useEffect(() => {
    if (validations)
      validateForm(
        name => root.current.querySelector(`input[name="${name}"]`),
        validations
      )
  }, [validations])

  return (
    <form
      ref={root}
      onSubmit={e => {
        let data

        if (validations) {
          data = validateForm(name => e.target.elements[name], validations)
        } else if (names) {
          data = names.reduce((acc, name) => {
            acc[name] = e.target.elements[name].value
            return acc
          }, {})
        }

        onSubmit(e, data)
      }}
      {...restProps}
    />
  )
}

Form.propTypes = propTypes

export default Form
